// PLAY03 guide overlay: channels with Now/Next inside the player. Choosing one zaps the same player (the controller
// releases the old lease before it opens the new one, so there is never a second lease). Same channel data, order and
// hidden/excluded filtering as Live TV (features/live/data.ts). Listbox with aria-activedescendant so it stays keyboard
// navigable while virtualized; Escape closes.
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { X } from 'lucide-react';
import type { Channel } from '@tuned/contracts';
import { useActiveConnection } from '../../app/session.ts';
import { useUserState } from '../../store/index.ts';
import { formatTime, resolveTimeZone, useNow } from '../../components/index.ts';
import { arrangeChannels, nowNext, useGuideBlocks, useLiveChannels } from '../live/data.ts';
import { nowWindow } from '../live/time.ts';
import type { PlayRequest } from './machine.ts';

const ROW = 64;

export function GuideOverlay({ currentChannelId, recent, onPlay, onClose }: {
  currentChannelId: string | null; recent: PlayRequest[]; onPlay(req: PlayRequest): void; onClose(): void;
}) {
  const id = useId();
  const connection = useActiveConnection();
  const user = useUserState();
  const prefs = user.state?.preferences ?? null;
  const timeZone = resolveTimeZone(prefs?.timezone);
  const shift = (prefs?.guideDisplayOffsetMinutes ?? 0) * 60_000; // display only; never sent anywhere
  const now = useNow(30_000);
  const { channels: all, query } = useLiveChannels(connection?.id);
  const channels = useMemo(
    () => arrangeChannels(all, { mode: 'all', category: null }, prefs, (list, itemId) => user.isInList(list, itemId)),
    [all, prefs, user],
  );
  const listRef = useRef<HTMLDivElement>(null);
  const v = useVirtualizer({ count: channels.length, getScrollElement: () => listRef.current, estimateSize: () => ROW, overscan: 6 });
  const items = v.getVirtualItems();
  const { from, to } = nowWindow(now);
  const guide = useGuideBlocks(connection?.id, channels, (items[0]?.index ?? 0) - 2, (items.at(-1)?.index ?? 10) + 2, from, to);
  const [active, setActive] = useState(-1);

  // Start on the channel that is playing, then keep focus in the list.
  useEffect(() => {
    if (active >= 0 || !channels.length) return;
    const i = Math.max(0, channels.findIndex((c) => c.id === currentChannelId));
    setActive(i);
    v.scrollToIndex(i, { align: 'center' });
  }, [channels, currentChannelId, active, v]);
  const hasList = channels.length > 0;
  useEffect(() => { if (hasList) listRef.current?.focus(); }, [hasList]);

  const program = (c: Channel) => {
    const g = guide.rows.get(c.id);
    return g?.status === 'ready' ? nowNext(g.programs, now) : { now: null, next: null };
  };
  const choose = (c: Channel) => {
    if (!c.available) return;
    const cur = program(c).now;
    onPlay({ sourceId: c.sourceId, mode: 'live', title: cur?.title ?? c.name, subtitle: c.name, channelId: c.id, programId: cur?.id, artworkId: c.logo });
    onClose();
  };
  const move = (i: number) => {
    const n = Math.max(0, Math.min(channels.length - 1, i));
    setActive(n);
    v.scrollToIndex(n);
  };
  const onListKey = (e: KeyboardEvent) => {
    const page = Math.max(1, Math.floor((listRef.current?.clientHeight ?? ROW * 8) / ROW) - 1);
    const keys: Record<string, () => void> = {
      ArrowDown: () => move(active + 1), ArrowUp: () => move(active - 1), PageDown: () => move(active + page), PageUp: () => move(active - page),
      Home: () => move(0), End: () => move(channels.length - 1), Enter: () => channels[active] && choose(channels[active]),
      ' ': () => channels[active] && choose(channels[active]),
    };
    const fn = keys[e.key];
    if (!fn) return;
    e.preventDefault();
    fn();
  };
  const others = recent.filter((r) => (r.channelId ?? r.sourceId) !== currentChannelId).slice(0, 6);

  let body;
  if (!connection) body = <p className="tp-guide-note">Connect a provider to see channels.</p>;
  else if (query.isPending) body = <p className="tp-guide-note">Loading channels…</p>;
  else if (!channels.length) body = <p className="tp-guide-note">{query.isError ? 'Channels couldn’t load.' : 'No channels to show.'}</p>;
  else body = (
    <div
      ref={listRef} className="tp-guide-list" role="listbox" tabIndex={0} aria-label="Channels"
      aria-activedescendant={active >= 0 ? `${id}-${active}` : undefined} onKeyDown={onListKey}
    >
      <div style={{ height: v.getTotalSize(), position: 'relative' }}>
        {items.map((it) => {
          const c = channels[it.index]!;
          const { now: cur, next } = program(c);
          const playing = c.id === currentChannelId;
          return (
            <div
              key={c.id} id={`${id}-${it.index}`} role="option" aria-selected={it.index === active} aria-disabled={!c.available || undefined}
              aria-setsize={channels.length} aria-posinset={it.index + 1} className="tp-guide-row"
              style={{ height: ROW, transform: `translateY(${it.start}px)` }}
              onClick={() => { setActive(it.index); choose(c); }}
            >
              <span className="tp-guide-num">{c.number ?? ''}</span>
              <span className="tp-guide-text">
                <span className="tp-guide-name">{c.name}{playing ? <span className="tp-badge">Playing</span> : null}</span>
                <span className="tp-guide-now">{cur ? <><span className="visually-hidden">Now: </span>{cur.title}</> : 'Schedule unavailable'}</span>
                {next ? <span className="tp-guide-next">Next {formatTime(Date.parse(next.start) + shift, timeZone)} · {next.title}</span> : null}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="tp-guide" role="dialog" aria-label="Channel guide" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}>
      <div className="tp-guide-head">
        <p className="tp-guide-title">Channel guide</p>
        <button type="button" className="tp-icon" aria-label="Close guide" onClick={onClose}><X aria-hidden="true" /></button>
      </div>
      {others.length ? (
        <nav className="tp-guide-recent" aria-label="Recent channels">
          {others.map((r) => (
            <button key={r.channelId ?? r.sourceId} type="button" className="tp-btn" onClick={() => { onPlay({ ...r, title: r.subtitle ?? r.title, programId: undefined }); onClose(); }}>
              {r.subtitle ?? r.title}
            </button>
          ))}
        </nav>
      ) : null}
      {body}
    </div>
  );
}
